import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { invitationsService, type InvitationDetails } from '../services/invitationsService';
import { ROLE_LABELS } from '../types/permissions';
import Layout from '../components/layout/Layout';

/**
 * Página de aceptación de invitación.
 * El usuario recibe un email con link /invitar/:token.
 *
 * Es pública: si el usuario no está logueado, puede ver los detalles de la
 * invitación y se le ofrece loguearse/registrarse. Al aceptar, si no hay
 * sesión, lo redirige a login con returnTo para volver automáticamente.
 */
const AcceptInvitationPage = () => {
  // El token puede llegar como path param (/invitar/:token) o como query param
  // (?token=...) cuando un cliente de email (Gmail/Outlook) reescribe el link.
  // Se sanitiza a hex puro (el token real es encode(gen_random_bytes(32),'hex')).
  const { token: pathToken } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const rawToken = pathToken ?? searchParams.get('token') ?? undefined;
  const token = rawToken ? decodeURIComponent(rawToken).replace(/[^a-f0-9]/gi, '') : undefined;
  const navigate = useNavigate();
  const { user, refreshPermissions } = useAuth();

  const [details, setDetails] = useState<InvitationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) return;
    invitationsService
      .getByToken(token)
      .then((d) => {
        if (!d) setError('Invitación no encontrada.');
        else if (d.accepted) setError('Esta invitación ya fue aceptada.');
        else if (d.expired) setError('La invitación ha expirado.');
        else setDetails(d);
      })
      .catch(() => setError('No se pudo cargar la invitación.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;

    // Si no está logueado, redirigir a login volviendo a esta página.
    if (!user) {
      navigate(`/login?redirect=/invitar/${token}`);
      return;
    }

    setActing(true);
    setError(null);
    try {
      await invitationsService.acceptInvitation(token);
      await refreshPermissions();
      setDone(true);
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err: any) {
      setError(err?.message ?? 'Error al aceptar la invitación.');
    } finally {
      setActing(false);
    }
  };

  const handleDecline = async () => {
    if (!token) return;
    setActing(true);
    try {
      await invitationsService.declineInvitation(token);
      navigate(user ? '/dashboard' : '/login');
    } catch {
      setError('No se pudo rechazar la invitación.');
      setActing(false);
    }
  };

  const content = (
    <>
      {loading && <p className="text-center text-sage">Cargando invitación…</p>}

      {error && (
        <div className="text-center">
          <h1 className="font-display text-xl font-semibold text-danger mb-2">Invitación inválida</h1>
          <p className="text-sage mb-4">{error}</p>
          <button
            onClick={() => navigate(user ? '/dashboard' : '/login')}
            className="bg-forest text-paper px-4 py-2 rounded-md hover:bg-forest-mid transition-colors"
          >
            {user ? 'Volver al panel' : 'Ir al login'}
          </button>
        </div>
      )}

      {done && (
        <div className="text-center">
          <h1 className="font-display text-xl font-semibold text-forest mb-2">¡Invitación aceptada!</h1>
          <p className="text-sage">Redirigiendo a tu panel…</p>
        </div>
      )}

      {details && !done && (
        <>
          <h1 className="font-display text-xl font-semibold text-ink mb-1">Fuiste invitado a unirte</h1>
          <p className="text-sage mb-4">
            Te invitaron como <strong>{ROLE_LABELS[details.role]}</strong> a{' '}
            <strong>{details.empresaNombre ?? 'la empresa'}</strong>.
          </p>
          <p className="text-sm text-sage mb-6">Invitación para: {details.email}</p>

          {!user && (
            <>
              <p className="text-sm bg-warning/10 border border-warning/20 text-warning rounded-md p-3 mb-4">
                Necesitás una cuenta para aceptar esta invitación.
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleAccept}
                  className="w-full bg-forest text-paper py-2.5 rounded-md font-medium hover:bg-forest-mid transition-colors"
                >
                  Ya tengo cuenta — Iniciar sesión
                </button>
                <button
                  onClick={() =>
                    navigate(
                      `/login?redirect=/invitar/${token}&mode=signup&email=${encodeURIComponent(details.email)}`,
                    )
                  }
                  className="w-full border border-forest text-forest py-2.5 rounded-md font-medium hover:bg-forest/5 transition-colors"
                >
                  No tengo cuenta — Crear cuenta nueva
                </button>
                <button
                  onClick={handleDecline}
                  disabled={acting}
                  className="text-sm text-sage hover:text-ink py-1"
                >
                  Rechazar invitación
                </button>
              </div>
            </>
          )}

          {user && (
            <div className="flex gap-3">
              <button
                onClick={handleAccept}
                disabled={acting}
                className="flex-1 bg-forest text-paper py-2.5 rounded-md font-medium hover:bg-forest-mid disabled:opacity-50 transition-colors"
              >
                {acting ? 'Procesando…' : 'Aceptar invitación'}
              </button>
              <button
                onClick={handleDecline}
                disabled={acting}
                className="px-4 py-2.5 border border-hairline bg-paper-2 text-ink rounded-md font-medium hover:bg-hairline/50 disabled:opacity-50 transition-colors"
              >
                Rechazar
              </button>
            </div>
          )}
        </>
      )}
    </>
  );

  // Si hay sesión, mostrar dentro del Layout normal (con sidebar/header).
  // Si no, mostrar standalone (sin navegación que rompería sin auth).
  return user ? (
    <Layout>
      <div className="max-w-md mx-auto mt-10 rounded-lg border border-hairline bg-white shadow-card p-8">{content}</div>
    </Layout>
  ) : (
    <div className="min-h-screen bg-[var(--cm-paper)] flex items-center justify-center p-4">
      <div className="max-w-md w-full rounded-lg border border-hairline bg-white shadow-card p-8">{content}</div>
    </div>
  );
};

export default AcceptInvitationPage;
